import { drawMediaFrame } from './drawClip.js'
import { blendTransition } from './transitions.js'
import { configureHighQualityContext } from '../export/qualitySettings.js'

const scratchCanvases = new Map()

const getScratchCanvas = (key, canvasSize) => {
    let canvas = scratchCanvases.get(key)
    if (!canvas) {
        canvas = document.createElement('canvas')
        scratchCanvases.set(key, canvas)
    }
    if (canvas.width !== canvasSize.width) canvas.width = canvasSize.width
    if (canvas.height !== canvasSize.height) canvas.height = canvasSize.height
    configureHighQualityContext(canvas.getContext('2d'))
    return canvas
}

// Single clip, no transition in progress. `cropPreview: true` shows the *full* source (ignoring
// cropRect, "contain" fit) - used while the crop tool is open so the user can see the whole frame
// to pick a region from. Otherwise each clip renders its OWN cropRect exactly as picked (ratio
// buttons, width/height px, or dragging - see ClipToolPanel.vue/CropSelector.vue), fit per its OWN
// fillMode: 'contain' (Ajuster, default) never crops beyond what was picked, letterboxing the gap
// instead (`background`) if that selection's ratio doesn't match the shared canvas; 'cover'
// (Remplir) zooms to fill the canvas edge to edge instead, cropping whatever overflows. Each
// clip's own cropRect/fillMode is untouched by editing any *other* clip - only the shared canvas
// ratio (MediaEditor.vue's resizeCanvasToRatio) is timeline-wide, which is exactly what lets a
// clip nobody's currently editing keep its own crop and simply adapt (letterbox/pillarbox) to
// whatever the final format ends up being.
export const renderClipFrame = (ctx, mediaEl, canvasSize, clip, { cropPreview = false, background = null } = {}) => {
    if (!clip) {
        drawMediaFrame(ctx, mediaEl, canvasSize, undefined, { fit: 'contain', background })
        return
    }
    if (cropPreview) {
        // Rotation/flip ignored here too - the crop selector overlay assumes a plain axis-aligned
        // contain-fit box; showing rotation while cropping would make its handles misaligned.
        //
        // background IS forwarded, so the letterbox bars show the real blurred/solid fill rather
        // than plain black. Only Ajuster ever has bars here: Remplir crops against a frame shaped
        // to the source itself (VideoPreviewCanvas.vue's cropWorkspaceSize), where the media fills
        // the frame exactly and needsLetterbox is false, so this makes no difference there. And in
        // Ajuster those bars aren't workspace chrome - they're exactly what gets exported.
        drawMediaFrame(ctx, mediaEl, canvasSize, { cropRect: { x: 0, y: 0, width: 1, height: 1 } }, { fit: 'contain', background })
    } else {
        drawMediaFrame(ctx, mediaEl, canvasSize, clip.transform, { fit: clip.transform.fillMode || 'contain', background })
    }
}

// Crossfade/wipe/slide between two clips during their overlapping transition window - each side
// is independently fit (per its own fillMode, see renderClipFrame above) + background-filled
// before blending, so a 'contain' clip still shows letterbox bars instead of an over-crop.
export const renderTransitionFrame = (ctx, { fromMediaEl, fromClip, toMediaEl, toClip, canvasSize, type, progress, direction, background = null }) => {
    const fromCanvas = getScratchCanvas('from', canvasSize)
    const toCanvas = getScratchCanvas('to', canvasSize)

    drawMediaFrame(fromCanvas.getContext('2d'), fromMediaEl, canvasSize, fromClip ? fromClip.transform : undefined, { fit: (fromClip && fromClip.transform.fillMode) || 'contain', background })
    drawMediaFrame(toCanvas.getContext('2d'), toMediaEl, canvasSize, toClip ? toClip.transform : undefined, { fit: (toClip && toClip.transform.fillMode) || 'contain', background })

    blendTransition(ctx, { fromCanvas, toCanvas, canvasSize, type, progress, direction })
}

// Draws the (already up to date) overlay layer - a transparent canvas containing text/shapes/
// widgets rendered by fabric.js - on top of the current video frame.
export const renderOverlayLayer = (ctx, overlayCanvasEl, canvasSize) => {
    if (!overlayCanvasEl) return
    ctx.drawImage(overlayCanvasEl, 0, 0, canvasSize.width, canvasSize.height)
}
